import { useEffect, useRef, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_MS = 30000;

/**
 * Live counter of Kairos engagements. Pulls the real total from
 * /api/activations (contacts + pilot applications + baseline), animates
 * from the previous value to the new value, and re-polls every 30s so
 * fresh submissions show up without a page refresh.
 */
export const LiveActivationsBadge = () => {
    const [count, setCount] = useState(0);
    const [justUpdated, setJustUpdated] = useState(false);
    const [status, setStatus] = useState("live"); // "live" | "offline"
    const rafRef = useRef(null);
    const currentRef = useRef(0);
    const firstLoadRef = useRef(true);

    useEffect(() => {
        let cancelled = false;

        const animateTo = (target) => {
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            const start = currentRef.current;
            const delta = target - start;
            if (delta === 0) return;
            const duration = firstLoadRef.current ? 1800 : 900;
            const t0 = performance.now();
            const step = (now) => {
                const t = Math.min(1, (now - t0) / duration);
                const eased = 1 - Math.pow(1 - t, 3);
                const value = Math.round(start + delta * eased);
                currentRef.current = value;
                setCount(value);
                if (t < 1) rafRef.current = requestAnimationFrame(step);
                else firstLoadRef.current = false;
            };
            rafRef.current = requestAnimationFrame(step);
        };

        const fetchCount = async () => {
            try {
                const res = await fetch(`${API}/activations`, { cache: "no-store" });
                if (!res.ok) throw new Error("bad status");
                const data = await res.json();
                if (cancelled) return;
                const next = typeof data.total === "number" ? data.total : 0;
                setStatus("live");
                if (next !== currentRef.current) {
                    if (!firstLoadRef.current) {
                        setJustUpdated(true);
                        setTimeout(() => setJustUpdated(false), 1400);
                    }
                    animateTo(next);
                }
            } catch {
                if (!cancelled) setStatus("offline");
            }
        };
        fetchCount();
        const id = setInterval(fetchCount, POLL_MS);
        return () => {
            cancelled = true;
            clearInterval(id);
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, []);

    return (
        <div
            data-testid="live-activations-badge"
            className={`reveal mt-16 relative bg-[#F0493E]/5 border p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6 max-w-5xl transition-all duration-500 ${
                justUpdated
                    ? "border-[#F0493E] shadow-[0_0_0_1px_rgba(240,73,62,0.6),0_20px_60px_-15px_rgba(240,73,62,0.35)]"
                    : "border-[#F0493E]/25"
            }`}
        >
            <div className="flex items-center gap-4">
                <span className="relative inline-flex h-3 w-3">
                    <span
                        className={`absolute inline-flex h-full w-full rounded-full ${
                            status === "live" ? "bg-[#F0493E]/60 animate-ping" : "bg-slate-500/40"
                        }`}
                    />
                    <span
                        className={`relative inline-flex rounded-full h-3 w-3 ${
                            status === "live" ? "bg-[#F0493E]" : "bg-slate-500"
                        }`}
                    />
                </span>
                <div>
                    <div
                        data-testid="live-activations-status"
                        className={`text-[10px] font-bold uppercase tracking-[0.32em] ${
                            status === "live" ? "text-[#F0493E]" : "text-slate-500"
                        }`}
                    >
                        {status === "live" ? "Live · Field Activations" : "Offline · Cached"}
                    </div>
                    <div className="mt-1 text-xs md:text-sm text-slate-400 font-medium">
                        Real-time counter — pilot applications, partner conversations & field engagements
                    </div>
                </div>
            </div>
            <div className="flex items-baseline gap-3">
                <div
                    data-testid="live-activations-count"
                    className={`font-display font-extrabold text-5xl md:text-6xl tnum tracking-tighter transition-colors duration-500 ${
                        justUpdated ? "text-[#F0493E]" : "text-white"
                    }`}
                >
                    {count.toLocaleString()}
                </div>
                <div className="text-xs md:text-sm font-bold uppercase tracking-[0.22em] text-slate-400">
                    Activations to date
                </div>
            </div>
        </div>
    );
};
